if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.initializeColorMedia();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    initializeColorMedia() {
      const config = this.querySelector('[data-color-gallery-config]');
      if (!config) return;
      this.colorConfig = JSON.parse(config.textContent);
      const normalize = value => String(value || '').trim().toLowerCase();
      this.colorMedia = this.colorConfig.media.map(media => {
        // Only an exact option value before " | " is a color label.
        const label = normalize(media.alt.split(' | ')[0]);
        const color = this.colorConfig.colors.find(value => normalize(value) === label);
        return {...media, color: color ? normalize(color) : null};
      });
      if (!this.colorMedia.some(media => media.color)) return;
      this.colorFilterEnabled = true;
      this.classList.add('elsvin-color-gallery');
      const thumbnails = this.elements.thumbnails?.querySelector('.thumbnail-list');
      if (thumbnails) {
        thumbnails.setAttribute('data-color-filter-thumbnails', '');
        const jq = window.jQuery;
        if (jq && thumbnails.classList.contains('slick-initialized')) jq(thumbnails).slick('unslick');
      }
      // Group filtering replaces Koka's single-variant-image hiding rule.
      this.querySelectorAll('.product__media-item--variant, .thumbnail-list_item--variant').forEach(item => {
        item.classList.remove('product__media-item--variant', 'thumbnail-list_item--variant');
      });
      requestAnimationFrame(() => this.filterColorMedia(this.colorConfig.options, null));
    }

    filterColorMedia(options, variant) {
      if (!this.colorFilterEnabled) return false;
      const color = String(options?.[this.colorConfig.position] || '').trim().toLowerCase();
      const matches = this.colorMedia.filter(media => media.color === color);
      // Incomplete tagging must not leave the customer with a blank gallery.
      const visible = matches.length
        ? this.colorMedia.filter(media => media.color === color || !media.color)
        : this.colorMedia;
      const allowed = new Set(visible.map(media => String(media.id)));
      const modal = document.getElementById(`ProductModal-${this.colorConfig.section}`);
      this.colorMedia.forEach(media => {
        const id = `${this.colorConfig.section}-${media.id}`;
        const hidden = !allowed.has(String(media.id));
        const slide = this.elements.viewer.querySelector(`[data-media-id="${id}"]`);
        const thumb = this.elements.thumbnails?.querySelector(`[data-target="${id}"]`);
        const zoom = modal?.querySelector(`[data-media-id="${media.id}"]`);
        [slide, thumb, zoom].forEach(item => {
          if (!item) return;
          item.classList.toggle('elsvin-color-hidden', hidden);
          if (hidden) item.classList.remove('is-active', 'active');
        });
        zoom?.classList.remove('product__media-item--variant');
      });
      const featured = variant?.featured_media?.id;
      const first = matches[0] || visible[0];
      if (!first) return false;
      const activeId = featured && allowed.has(String(featured)) ? featured : first.id;
      this.setActiveMedia(`${this.colorConfig.section}-${activeId}`, false, true);
      this.elements.viewer.resetPages?.();
      this.elements.thumbnails?.resetPages?.();
      return true;
    }

    onSlideChanged(event) {
      if (!event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, colorChange = false) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia || activeMedia.classList.contains('elsvin-color-hidden')) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails || colorChange) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!colorChange && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
